import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MdAutoAwesome, MdCheckCircle, MdError, MdRefresh, MdOpenInNew } from 'react-icons/md';
import { PageShell, Badge } from '../../components/Ui';
import { ipApi } from '../../services/api';
import { useHmsConfig } from '../../hooks/useHmsRecords';

const KEY_HELP_URL = 'https://aistudio.google.com/apikey';

export default function AiSettingsPage() {
  const { config, save, loading: cfgLoading } = useHmsConfig('ai-settings');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const enabled = config?.enabled !== false;

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const s = await ipApi.aiStatus();
      setStatus(s);
    } catch (e) {
      setErr(e.message || 'Could not load AI status');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const setEnabled = async (next) => {
    setSaving(true);
    setMsg('');
    try {
      await save({ ...config, enabled: next, updatedAt: new Date().toISOString() });
      setMsg(next ? 'AI features enabled for this hospital.' : 'AI features disabled — doctors will see rule-based assist only.');
    } catch (e) {
      setMsg(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await ipApi.aiClinicalAssist({
        complaint: 'Fever and cough for 2 days',
        age: '35',
        gender: 'M',
      });
      setTestResult({
        ok: true,
        sample: (res?.suggestions?.diagnosis || []).slice(0, 2).join(', ') || 'OK',
      });
    } catch (e) {
      setTestResult({
        ok: false,
        message: e.message || 'Test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const connected = !!status?.configured;
  const live = connected && enabled;

  return (
    <PageShell
      title="AI Assist"
      subtitle="Clinical Assist & discharge drafts — doctor must always review"
      actions={
        <button type="button" className="btn btn-secondary" onClick={loadStatus} disabled={loading}>
          <MdRefresh /> Refresh status
        </button>
      }
    >
      <div className="stat-grid" style={{ marginBottom: 16 }}>
        <div className="card" style={{ padding: 16 }}>
          <div className="hint" style={{ marginBottom: 6 }}>Server key</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 18 }}>
            {loading ? 'Checking…' : connected ? (
              <><MdCheckCircle color="#16a34a" /> Connected</>
            ) : (
              <><MdError color="#dc2626" /> Not configured</>
            )}
          </div>
          <div className="hint" style={{ marginTop: 6 }}>
            {connected ? 'AI API key is set on the server' : 'Add AI API key on Lambda environment'}
          </div>
        </div>
        <div className="card" style={{ padding: 16 }}>
          <div className="hint" style={{ marginBottom: 6 }}>This hospital</div>
          <div style={{ fontWeight: 700, fontSize: 18 }}>
            {cfgLoading ? '…' : enabled ? 'Enabled' : 'Disabled'}
          </div>
          <div className="hint" style={{ marginTop: 6 }}>
            Managers can turn AI off without removing the server key.
          </div>
        </div>
        <div className="card" style={{ padding: 16 }}>
          <div className="hint" style={{ marginBottom: 6 }}>Live for doctors</div>
          <div style={{ fontWeight: 700, fontSize: 18 }}>
            {live ? <Badge tone="success">Active</Badge> : <Badge tone="warning">Rules only</Badge>}
          </div>
          <div className="hint" style={{ marginTop: 6 }}>
            EMR Clinical Assist + Discharge AI draft
          </div>
        </div>
      </div>

      {err && <p className="hint" style={{ color: '#b91c1c' }}>{err}</p>}
      {msg && <p className="hint" style={{ color: '#0d6e6a' }}>{msg}</p>}

      <div className="card mb" style={{ padding: 20 }}>
        <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <MdAutoAwesome /> Hospital AI toggle
        </h3>
        <p className="hint">
          When disabled, Clinical Assist falls back to built-in rules and Discharge hides AI draft.
          The API key stays on the server.
        </p>
        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
          <button
            type="button"
            className={`btn ${enabled ? 'btn-primary' : 'btn-secondary'}`}
            disabled={saving || cfgLoading}
            onClick={() => setEnabled(true)}
          >
            Enable AI
          </button>
          <button
            type="button"
            className={`btn ${!enabled ? 'btn-primary' : 'btn-secondary'}`}
            disabled={saving || cfgLoading}
            onClick={() => setEnabled(false)}
          >
            Disable AI
          </button>
        </div>
      </div>

      {!connected && (
        <div className="card mb" style={{ padding: 20, borderColor: '#fde68a', background: '#fffbeb' }}>
          <h3 style={{ marginTop: 0 }}>Turn on AI (one-time setup)</h3>
          <ol style={{ margin: '8px 0 0', paddingLeft: 20, lineHeight: 1.7, fontSize: 14 }}>
            <li>
              Create a free AI API key{' '}
              <a href={KEY_HELP_URL} target="_blank" rel="noreferrer">
                here <MdOpenInNew style={{ verticalAlign: 'middle' }} />
              </a>
              .
            </li>
            <li>
              <strong>AWS Console:</strong> Lambda → <code>HCEN_IP_API</code> → Configuration → Environment variables → Edit → add your AI API key → Save.
            </li>
            <li>Wait ~10 seconds, then click <strong>Refresh status</strong> above.</li>
            <li>Click <strong>Test AI</strong> below. If it works, doctors get AI in EMR &amp; Discharge.</li>
          </ol>
          <p className="hint" style={{ marginTop: 12 }}>
            Never put the API key in the website frontend.
          </p>
        </div>
      )}

      {connected && (
        <div className="card mb" style={{ padding: 20 }}>
          <h3 style={{ marginTop: 0 }}>Connection test</h3>
          <p className="hint">Sends a sample “fever and cough” complaint to AI (no patient data).</p>
          <button type="button" className="btn btn-primary" disabled={testing || !enabled} onClick={runTest}>
            {testing ? 'Testing…' : 'Test AI'}
          </button>
          {!enabled && <p className="hint" style={{ marginTop: 8 }}>Enable AI first to run the test.</p>}
          {testResult?.ok && (
            <p style={{ marginTop: 12, color: '#15803d', fontWeight: 600 }}>
              ✓ Working — sample: {testResult.sample}
            </p>
          )}
          {testResult && !testResult.ok && (
            <p style={{ marginTop: 12, color: '#b91c1c' }}>✗ {testResult.message}</p>
          )}
        </div>
      )}

      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ marginTop: 0 }}>Where AI appears</h3>
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8, fontSize: 14 }}>
          <li>
            <Link to="/hms/emr">EMR / Clinical Assist</Link> — diagnosis, labs, Rx suggestions (Apply only)
          </li>
          <li>
            <Link to="/hms/discharge">Discharge Summary</Link> — “AI draft” button
          </li>
        </ul>
        <p className="hint" style={{ marginTop: 12 }}>
          AI never auto-finalizes clinical records. Doctors must review and confirm.
        </p>
      </div>
    </PageShell>
  );
}
